import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Settings, Brain, Mic, MessageSquare, Zap, Clock, 
  Check, RefreshCw, Trash2, Save, Camera, User, Edit2, X,
  Sparkles, Volume2, Gauge, ChevronDown, Upload, Play, MessagesSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Photos par défaut pour les agents (avatars professionnels)
const DEFAULT_AVATARS = {
  equin: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face",
  oenologue: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
  comptable: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
  subventions: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face",
  direction: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
  commercial: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
  technique: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=150&h=150&fit=crop&crop=face",
  juridique: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face",
  rh: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
  marketing: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face",
};

export default function AgentsConfigPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialAgentId = location.state?.selectedAgent;
  const fileInputRef = useRef(null);
  
  const [agents, setAgents] = useState([]);
  const [options, setOptions] = useState({});
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editConfig, setEditConfig] = useState({});
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('identity');

  useEffect(() => {
    fetchAgents();
  }, []);
  
  useEffect(() => {
    if (initialAgentId && agents.length > 0 && !selectedAgent) {
      const agent = agents.find(a => a.agent_id === initialAgentId);
      if (agent) {
        selectAgent(agent);
      }
    }
  }, [initialAgentId, agents]);

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/agents-config/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAgents(data.agents || []);
      setOptions(data.options || {});
      setLoading(false);
    } catch (err) {
      console.error('Erreur:', err);
      setLoading(false);
    }
  };

  const selectAgent = (agent) => {
    setSelectedAgent(agent);
    setEditConfig({
      first_name: agent.first_name || '',
      last_name: agent.last_name || '',
      job_title: agent.job_title || agent.description || '',
      bio: agent.bio || '',
      custom_avatar_url: agent.avatar_url || '',
      provider: agent.provider,
      model: agent.model,
      style: agent.style,
      tone: agent.tone,
      voice: agent.voice,
      reactivity: agent.reactivity,
      memory: agent.memory,
      brain_integration: agent.brain_integration,
      custom_prompt: agent.custom_prompt || '',
      temperature: agent.temperature || 0.7,
    });
    setShowEditDialog(true);
  };

  const saveConfig = async () => {
    if (!selectedAgent) return;
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Construire le nom personnalisé à partir du prénom/nom
      const customName = editConfig.first_name && editConfig.last_name 
        ? `${editConfig.first_name} ${editConfig.last_name}`
        : editConfig.first_name || null;
      
      const res = await fetch(`${API_URL}/api/agents-config/agents/${selectedAgent.agent_id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...editConfig,
          custom_name: customName
        })
      });
      
      if (res.ok) {
        toast.success('Configuration sauvegardée !');
        fetchAgents();
        setShowEditDialog(false);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
    
    setSaving(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Convertir en base64 pour prévisualisation (en prod, uploader sur un serveur)
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditConfig({ ...editConfig, custom_avatar_url: reader.result });
    };
    reader.readAsDataURL(file);
    
    toast.success('Photo mise à jour (aperçu)');
  };

  const getAgentAvatar = (agent) => {
    return agent.avatar_url || DEFAULT_AVATARS[agent.agent_id] || DEFAULT_AVATARS.direction;
  };

  const getAgentDisplayName = (agent) => {
    if (agent.first_name && agent.last_name) {
      return `${agent.first_name} ${agent.last_name}`;
    }
    if (agent.first_name) return agent.first_name;
    return agent.display_name;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="agents-config-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Brain className="w-7 h-7 text-green-600" />
          Mon Équipe IA
        </h1>
        <p className="text-gray-600 mt-1">
          Personnalisez vos assistants : photo, nom, intelligence et style de communication
        </p>
      </div>

      {/* Grille des agents - Style "Équipe" */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
        {agents.map((agent) => (
          <div
            key={agent.agent_id}
            className="group"
            data-testid={`agent-card-${agent.agent_id}`}
          >
            <div className="relative">
              {/* Photo de profil */}
              <div className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow">
                <img
                  src={getAgentAvatar(agent)}
                  alt={agent.display_name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.display_name)}&size=200&background=${agent.color?.replace('#', '')}&color=fff`;
                  }}
                />
                
                {/* Overlay avec boutons */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-3">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    {/* Bouton Parler */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/agents', { state: { preselectedAgentId: agent.agent_id } });
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-full p-3 shadow-lg transition-colors"
                      title="Parler à cet agent"
                      data-testid={`talk-to-agent-${agent.agent_id}`}
                    >
                      <MessagesSquare className="w-5 h-5" />
                    </button>
                    {/* Bouton Configurer */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAgent(agent);
                      }}
                      className="bg-white hover:bg-gray-100 text-gray-700 rounded-full p-3 shadow-lg transition-colors"
                      title="Configurer cet agent"
                      data-testid={`config-agent-${agent.agent_id}`}
                    >
                      <Settings className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* Badge personnalisé */}
                {agent.is_customized && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full shadow">
                      Personnalisé
                    </div>
                  </div>
                )}
                
                {/* Indicateur de statut */}
                <div className="absolute bottom-2 right-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow animate-pulse"></div>
                </div>
              </div>
              
              {/* Infos sous la photo */}
              <div className="mt-3 text-center">
                <h3 className="font-semibold text-gray-900 truncate">
                  {getAgentDisplayName(agent)}
                </h3>
                <p className="text-xs text-gray-500 truncate">
                  {agent.job_title || agent.description}
                </p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <Sparkles className="w-3 h-3 text-amber-500" />
                  <span className="text-xs text-gray-400">{agent.model_name}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dialog de configuration */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-full overflow-hidden border-2"
                style={{ borderColor: selectedAgent?.color }}
              >
                <img
                  src={editConfig.custom_avatar_url || getAgentAvatar(selectedAgent || {})}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedAgent?.display_name || 'Agent')}&size=100`;
                  }}
                />
              </div>
              <div>
                <span>Configurer {selectedAgent?.display_name}</span>
                <p className="text-sm font-normal text-gray-500">{selectedAgent?.description}</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="identity" className="flex items-center gap-1">
                <User className="w-4 h-4" /> Identité
              </TabsTrigger>
              <TabsTrigger value="intelligence" className="flex items-center gap-1">
                <Brain className="w-4 h-4" /> Intelligence
              </TabsTrigger>
              <TabsTrigger value="communication" className="flex items-center gap-1">
                <MessageSquare className="w-4 h-4" /> Communication
              </TabsTrigger>
            </TabsList>

            {/* Onglet Identité */}
            <TabsContent value="identity" className="space-y-4 mt-4">
              {/* Photo de profil */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-gray-100 shadow-lg">
                    <img
                      src={editConfig.custom_avatar_url || getAgentAvatar(selectedAgent || {})}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedAgent?.display_name || 'Agent')}&size=200`;
                      }}
                    />
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-2 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <p className="text-sm text-gray-600">
                    Choisissez une photo pour personnaliser votre assistant
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-1" /> Importer
                    </Button>
                    <Input
                      placeholder="Ou collez une URL d'image..."
                      value={editConfig.custom_avatar_url || ''}
                      onChange={(e) => setEditConfig({...editConfig, custom_avatar_url: e.target.value})}
                      className="text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Nom et prénom */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Prénom</label>
                  <Input
                    placeholder="Ex: Marie"
                    value={editConfig.first_name || ''}
                    onChange={(e) => setEditConfig({...editConfig, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Nom</label>
                  <Input
                    placeholder="Ex: Dupont"
                    value={editConfig.last_name || ''}
                    onChange={(e) => setEditConfig({...editConfig, last_name: e.target.value})}
                  />
                </div>
              </div>

              {/* Poste */}
              <div>
                <label className="text-sm font-medium text-gray-700">Poste / Fonction</label>
                <Input
                  placeholder="Ex: Comptable, Vétérinaire équin, Conseiller PAC..."
                  value={editConfig.job_title || ''}
                  onChange={(e) => setEditConfig({...editConfig, job_title: e.target.value})}
                />
              </div>

              {/* Bio */}
              <div>
                <label className="text-sm font-medium text-gray-700">Bio (optionnel)</label>
                <textarea
                  placeholder="Courte description de l'assistant..."
                  value={editConfig.bio || ''}
                  onChange={(e) => setEditConfig({...editConfig, bio: e.target.value})}
                  className="w-full p-2 border rounded-lg text-sm resize-none"
                  rows={2}
                />
              </div>
            </TabsContent>

            {/* Onglet Intelligence */}
            <TabsContent value="intelligence" className="space-y-4 mt-4">
              {/* Modèle IA */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Modèle d'Intelligence
                </label>
                <Select
                  value={`${editConfig.provider}:${editConfig.model}`}
                  onValueChange={(v) => {
                    const [provider, model] = v.split(':');
                    setEditConfig({...editConfig, provider, model});
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(options.models || {}).map(([provider, models]) => (
                      <React.Fragment key={provider}>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-400 uppercase">
                          {provider}
                        </div>
                        {models.map((m) => (
                          <SelectItem key={`${provider}:${m.id}`} value={`${provider}:${m.id}`}>
                            <div className="flex items-center gap-2">
                              <span>{m.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {m.tier}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Mémoire */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  Mémoire
                </label>
                <Select
                  value={editConfig.memory}
                  onValueChange={(v) => setEditConfig({...editConfig, memory: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options.memory_options || []).map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        <div>
                          <span>{m.name}</span>
                          <span className="text-xs text-gray-400 ml-2">{m.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Intégration Cerveau */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-700">Partage avec le Cerveau</p>
                  <p className="text-xs text-gray-500">Les conversations enrichissent tous les agents</p>
                </div>
                <button
                  onClick={() => setEditConfig({...editConfig, brain_integration: !editConfig.brain_integration})}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    editConfig.brain_integration ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    editConfig.brain_integration ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </TabsContent>

            {/* Onglet Communication */}
            <TabsContent value="communication" className="space-y-4 mt-4">
              {/* Style */}
              <div>
                <label className="text-sm font-medium text-gray-700">Style de réponse</label>
                <Select
                  value={editConfig.style}
                  onValueChange={(v) => setEditConfig({...editConfig, style: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options.styles || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span>{s.icon} {s.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ton */}
              <div>
                <label className="text-sm font-medium text-gray-700">Ton</label>
                <Select
                  value={editConfig.tone}
                  onValueChange={(v) => setEditConfig({...editConfig, tone: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options.tones || []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span>{t.emoji} {t.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Voix TTS */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-purple-500" />
                  Voix (synthèse vocale)
                </label>
                <Select
                  value={editConfig.voice}
                  onValueChange={(v) => setEditConfig({...editConfig, voice: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options.voices || []).map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        <div className="flex items-center gap-2">
                          <span>{v.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {v.gender === 'female' ? '♀' : v.gender === 'male' ? '♂' : '◎'}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Réactivité */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-orange-500" />
                  Longueur des réponses
                </label>
                <Select
                  value={editConfig.reactivity}
                  onValueChange={(v) => setEditConfig({...editConfig, reactivity: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options.reactivity_levels || []).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        <span>{r.name} - {r.description}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6 flex gap-2">
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                navigate('/agents', { state: { preselectedAgentId: selectedAgent?.agent_id } });
              }}
              className="border-green-600 text-green-600 hover:bg-green-50"
              data-testid="talk-to-agent-dialog-btn"
            >
              <MessagesSquare className="w-4 h-4 mr-2" />
              Parler
            </Button>
            <Button onClick={saveConfig} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
